import { useState, useEffect } from "react";
import { FaCheck, FaEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import ExportOptions from "../../../../ExportOptions";
import TableSkeleton from "../../../../TableSkeleton";
import ConfirmActionModal from "../../../../ConfirmDeleteModal";
import OfficeExpenseItemModal from "./OfficeExpenseItemModal";

const OfficeExpenseItemList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const itemsPerPage = 10;
  
  // API Config
  const API_URL = "https://api.muktihospital.com/api/hospital/office-expense-item";
  const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_URL, {
        headers: { "x-api-key": API_KEY },
      });
      if (response.data && Array.isArray(response.data.items)) {
        setItems(response.data.items);
      } else {
        setItems([]);
      }
    } catch (error) {
      console.error("Error fetching items:", error);
      toast.error("Failed to load items.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [refreshTrigger]);

  const filteredItems = items.filter(
    (item) => (item.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const currentData = filteredItems
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    .map((item, index) => ({
      ...item,
      displayId: (currentPage - 1) * itemsPerPage + index + 1,
    }));

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(currentData.map((item) => item.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleDelete = async (ids) => {
    try {
      for (const id of ids) {
        await axios.delete(`${API_URL}/delete/${id}`, {
          headers: { "x-api-key": API_KEY },
        });
      }
      toast.success("Deleted successfully!");
      setSelectedRows([]);
      setDeleteModalOpen(false);
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      toast.error("Failed to delete.");
    }
  };

  const handleSave = async (savedItem, isEditMode) => {
    try {
      if (isEditMode) {
        await axios.put(`${API_URL}/edit/${savedItem.id}`, savedItem, {
          headers: { "x-api-key": API_KEY },
        });
        toast.success("Updated successfully!");
      } else {
        await axios.post(`${API_URL}/add`, savedItem, {
          headers: { "x-api-key": API_KEY },
        });
        toast.success("Added successfully!");
      }
      setModalOpen(false);
      setItemToEdit(null);
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      const errorMsg = error.response?.data?.error || "Failed to save.";
      toast.error(errorMsg);
    }
  };

  const headers = ["Serial No", "Expense Item Name"];
  const dataMapper = (item) => [item.displayId, item.name].join("\t");
  const skeletonColumns = [{ width: "40px", className: "mx-3" }, { width: "200px", className: "px-3" }, { width: "80px", className: "ml-auto" }];

  return (
    <div>
      <ToastContainer />
      <div className="bg-white shadow-sm rounded-lg">
        {/* Header Section */}
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Expense Item List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search item..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="flex items-center w-full justify-between gap-3">
              <button
                onClick={() => { setItemToEdit(null); setModalOpen(true); }}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              >
                + Add Item
              </button>
              <ExportOptions
                data={currentData} 
                dropdownOpen={dropdownOpen} 
                toggleDropdown={() => setDropdownOpen(!dropdownOpen)} 
                title="Expense Items" 
                headers={headers} 
                dataMapper={dataMapper} 
              />
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div>
          <div className="overflow-x-auto print-wrapper">
            {loading ? (
              <div className="print-hidden">
                <table className="min-w-[600px] w-full divide-y divide-gray-200 print-table">
                  <tbody>
                    <TableSkeleton rowCount={itemsPerPage} columns={skeletonColumns} baseColor="#e5e7eb" highlightColor="#f3f4f6" />
                  </tbody>
                </table>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="p-5 text-center text-gray-600 print-hidden">
                No items found.
              </div>
            ) : (
              <div className="print-wrapper">
                <table className="min-w-[600px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]">
                          <input
                            type="checkbox"
                            className="hidden peer print-hidden"
                            onChange={handleSelectAll}
                            checked={currentData.length > 0 && currentData.every((item) => selectedRows.includes(item.id))}
                            id="selectAll"
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label htmlFor="selectAll" className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"></label>
                        </div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Serial No</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Expense Item Name</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((item, index) => (
                      <tr key={item.id} className="border-b hover:bg-gray-50">
                        <td className="text-left print-hidden-in-print">
                           <div className="relative left-3 -top-[3px]">
                            <input
                              type="checkbox"
                              id={`row-${index}`}
                              className="hidden peer"
                              checked={selectedRows.includes(item.id)}
                              onChange={() => handleRowSelect(item.id)}
                            />
                            <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                              <FaCheck className="text-slate-100" />
                            </span>
                             <label htmlFor={`row-${index}`} className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"></label>
                           </div>
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {item.displayId}
                        </td>
                        <td className="text-left font-inter font-medium text-sm text-M-heading-color py-5 px-3 capitalize">
                          {item.name}
                        </td>
                        <td className="p-3 text-right space-x-2 print-hidden-in-print">
                          <button
                            onClick={() => { setItemToEdit(item); setModalOpen(true); }}
                            className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => { setSelectedRows([item.id]); setDeleteModalOpen(true); }}
                            className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                            title="Delete"
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Pagination Section */}
          {totalPages > 1 && (
            <div className="p-5 text-end print-hidden">
               <div className="inline-flex items-center justify-between w-full">
                <p className="text-sm text-gray-600">
                    Records: {currentPage * itemsPerPage - itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredItems.length)} of {filteredItems.length}
                </p>
                <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                    <li>
                        <button className="table-pagination-item disabled:opacity-50" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}><MdKeyboardArrowLeft /></button>
                    </li>
                    {[...Array(totalPages)].map((_, i) => (
                        <li key={i}>
                            <button 
                                className={`table-pagination-item ${currentPage === i + 1 ? "bg-M-primary-color text-white" : ""}`} 
                                onClick={() => handlePageChange(i + 1)}
                            >
                                {i + 1}
                            </button>
                        </li>
                    ))}
                    <li>
                        <button className="table-pagination-item" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}><MdKeyboardArrowRight /></button>
                    </li>
                </ul>
               </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmActionModal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} onConfirm={handleDelete} ids={selectedRows} entityName="item" />
      <OfficeExpenseItemModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onSave={handleSave} itemToEdit={itemToEdit} />
    </div>
  );
};

export default OfficeExpenseItemList;